import { useState } from 'react';
import { useParams, useLocation } from 'wouter';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Building2,
  ShoppingCart,
  Edit,
  Plus,
  CreditCard,
  Calendar,
  DollarSign,
  Tag,
  BarChart3,
  FileText,
  Settings,
  LayoutGrid,
  MessageSquare,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormDrawer } from '@/components/ui/form-drawer';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Account {
  id: string;
  name: string;
  code?: string;
  accountNumber?: string;
  email?: string;
  phone?: string;
  industry?: string;
  website?: string;
  status?: string;
  tier?: string;
  territory?: string;
  parentAccountId?: string;
  notes?: string;
  customerClass?: string;
  creditHold?: boolean;
  leadSource?: string;
  externalId?: string;
  lastVisitDate?: string;
  createdAt?: string;
  paymentTerms?: string;
  currencyCode?: string;
  creditLimit?: string;
}

const statuses = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'on_hold', label: 'On Hold' },
];

const industries = [
  'Technology',
  'Healthcare',
  'Manufacturing',
  'Retail',
  'Finance',
  'Education',
  'Construction',
  'Transportation',
  'Hospitality',
  'Other',
];

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { sessionId } = useAuth();
  const [, setLocation] = useLocation();
  const [editOpen, setEditOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [formData, setFormData] = useState<Partial<Account>>({});

  const { data: account, isLoading } = useQuery({
    queryKey: ['account', id, sessionId],
    queryFn: async () => {
      const result = await api.get<{ account: Account }>(`/accounts/${id}`, sessionId);
      return result.account;
    },
    enabled: !!sessionId && !!id,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<Account>) => {
      return api.patch(`/accounts/${id}`, data, sessionId);
    },
    onSuccess: () => {
      setEditOpen(false);
      window.location.reload();
    },
  });

  const handleEditOpen = () => {
    if (account) {
      setFormData({
        name: account.name || '',
        code: account.code || '',
        accountNumber: account.accountNumber || '',
        email: account.email || '',
        phone: account.phone || '',
        industry: account.industry || '',
        website: account.website || '',
        status: account.status || '',
        tier: account.tier || '',
        territory: account.territory || '',
        notes: account.notes || '',
        customerClass: account.customerClass || '',
        creditHold: account.creditHold || false,
        leadSource: account.leadSource || '',
        externalId: account.externalId || '',
      });
    }
    setEditOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const normalizedData: Partial<Account> = {
      ...formData,
      code: formData.code ? formData.code.toUpperCase() : undefined,
    };
    updateMutation.mutate(normalizedData);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="space-y-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-64" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-[200px] w-full rounded-lg" />
            <Skeleton className="h-[200px] w-full rounded-lg" />
            <Skeleton className="h-[200px] w-full rounded-lg" />
          </div>
          <div className="space-y-6">
            <Skeleton className="h-[200px] w-full rounded-lg" />
            <Skeleton className="h-[200px] w-full rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Account not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Page Header */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation('/accounts')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight">{account.name}</h1>
            {account.accountNumber && (
              <p className="text-sm text-muted-foreground mt-1">
                Reference Number: {account.accountNumber}
              </p>
            )}
          </div>
          <Button variant="outline" size="sm">
            <MessageSquare className="h-4 w-4 sm:mr-2 text-pink-500" />
            <span className="hidden sm:inline">Interactions</span>
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('overview')}
            className={cn(
              'flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors',
              activeTab === 'overview'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <LayoutGrid className="h-4 w-4" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('pricing')}
            className={cn(
              'flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors',
              activeTab === 'pricing'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <DollarSign className="h-4 w-4" />
            Pricing
          </button>
          <button
            onClick={() => setActiveTab('po-processing')}
            className={cn(
              'flex items-center gap-2 px-4 py-2 border-b-2 font-medium text-sm transition-colors',
              activeTab === 'po-processing'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <FileText className="h-4 w-4" />
            PO Processing
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid gap-4 lg:grid-cols-3">
          {/* Left column (2/3 width) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Account · Commercial */}
            <Card>
              <CardHeader className="border-l-4 border-emerald-500">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-emerald-500" />
                    <CardTitle>Account · Commercial</CardTitle>
                  </div>
                  <Button variant="ghost" size="icon" onClick={handleEditOpen}>
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Company Name</p>
                    <p className="font-medium">{account.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Code</p>
                    <p className="font-mono text-sm font-semibold">
                      {account.code || <span className="text-muted-foreground italic">—</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Reference Number</p>
                    <p className="font-mono text-xs">
                      {account.accountNumber || <span className="text-muted-foreground italic">-</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Industry</p>
                    <p>
                      {account.industry ? (
                        account.industry.replace(/\b\w/g, (c) => c.toUpperCase())
                      ) : (
                        <span className="text-muted-foreground italic">-</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={account.status === 'active' ? 'default' : 'secondary'}>
                      {account.status || 'active'}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      Account
                    </Badge>
                    {account.creditHold && (
                      <Badge variant="destructive" className="text-xs">
                        Credit Hold
                      </Badge>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Tier</p>
                    <p className="capitalize">
                      {account.tier ? account.tier.replace(/_/g, '-') : <span className="text-muted-foreground italic">-</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Territory</p>
                    <p>{account.territory || <span className="text-muted-foreground italic">-</span>}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Customer Class</p>
                    <p className="capitalize">
                      {account.customerClass ? account.customerClass.replace(/_/g, ' ') : <span className="text-muted-foreground italic">-</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Lead Source</p>
                    <p>{account.leadSource || <span className="text-muted-foreground italic">-</span>}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Last Visit Date</p>
                    <p>
                      {account.lastVisitDate ? (
                        new Date(account.lastVisitDate).toLocaleDateString()
                      ) : (
                        <span className="text-muted-foreground italic">No visits logged</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created</p>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs">
                        {account.createdAt ? new Date(account.createdAt).toLocaleDateString() : '-'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    {account.email ? (
                      <a
                        href={`mailto:${account.email}`}
                        className="text-primary hover:underline truncate text-sm block"
                      >
                        {account.email}
                      </a>
                    ) : (
                      <p className="text-muted-foreground italic">-</p>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Phone</p>
                    <p className="text-sm">
                      {account.phone || <span className="text-muted-foreground italic">-</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Website</p>
                    {account.website ? (
                      <a
                        href={account.website.startsWith('http') ? account.website : `https://${account.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline truncate text-sm block"
                      >
                        {account.website}
                      </a>
                    ) : (
                      <p className="text-muted-foreground italic">-</p>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-3 border-t">
                  <p className="text-xs text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm whitespace-pre-wrap">
                    {account.notes || <span className="text-muted-foreground italic">No notes</span>}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Orders */}
            <Card>
              <CardHeader className="border-l-4 border-orange-500">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-orange-500" />
                  <CardTitle>Orders</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center h-16 text-muted-foreground">
                  <ShoppingCart className="h-6 w-6 mb-1 opacity-50" />
                  <p className="text-xs">No orders found</p>
                </div>
              </CardContent>
            </Card>

            {/* Integrations */}
            <Card>
              <CardHeader className="border-l-4 border-sky-500">
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-sky-500" />
                  <CardTitle>Integrations</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">External ID</p>
                    {account.externalId ? (
                      (() => {
                        const idx = account.externalId.indexOf(':');
                        const hasPrefix = idx > 0;
                        const prefix = hasPrefix ? account.externalId.slice(0, idx) : null;
                        const value = hasPrefix ? account.externalId.slice(idx + 1) : account.externalId;
                        return (
                          <div className="flex items-center gap-2 mt-1">
                            {prefix && (
                              <Badge variant="secondary" className="font-mono text-[10px] uppercase">
                                {prefix}
                              </Badge>
                            )}
                            <span className="font-mono text-xs">{value}</span>
                          </div>
                        );
                      })()
                    ) : (
                      <p className="text-xs font-mono text-muted-foreground italic mt-1">
                        Not connected to an external system
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right column (1/3 width) */}
          <div className="space-y-4">
            {/* Summary */}
            <Card>
              <CardHeader className="border-l-4 border-slate-500">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-slate-500" />
                  <CardTitle>Summary</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Orders</span>
                  <Badge variant="outline" className="text-xs">
                    0
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Sales Quotes</span>
                  <Badge variant="outline" className="text-xs">
                    0
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Opportunities</span>
                  <Badge variant="outline" className="text-xs">
                    0
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Contacts</span>
                  <Badge variant="outline" className="text-xs">
                    0
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Locations</span>
                  <Badge variant="outline" className="text-xs">
                    0
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Team Members</span>
                  <Badge variant="outline" className="text-xs">
                    0
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Billing Profile */}
            <Card>
              <CardHeader className="border-l-4 border-yellow-500">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-yellow-500" />
                    <CardTitle>Billing Profile</CardTitle>
                  </div>
                  <Button variant="ghost" size="icon">
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Payment Terms</p>
                    <p className="font-medium">{account.paymentTerms || 'NET30'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'pricing' && (
        <Card>
          <CardHeader className="border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-500" />
                <CardTitle>Price Overrides</CardTitle>
              </div>
              <Button variant="ghost" size="icon">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Tag className="h-10 w-10 mb-2 opacity-50" />
              <p className="text-sm font-medium">No price overrides</p>
              <p className="text-xs mt-1">Add SKU-level price overrides for this account</p>
            </div>
          </CardContent>
        </Card>
      )}

      {activeTab === 'po-processing' && (
        <Card>
          <CardHeader>
            <CardTitle>PO Processing</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">PO processing settings coming soon</p>
          </CardContent>
        </Card>
      )}

      {/* Edit Drawer */}
      <FormDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        title="Edit Account"
        description="Update the account information below."
        formId="edit-account"
        isPending={updateMutation.isPending}
        submitLabel="Save Changes"
        size="lg"
        data-testid="drawer-edit-account"
      >
        <form id="edit-account" onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="text-sm font-medium">Company Name *</label>
            <Input
              variant="flat"
              placeholder="Enter company name"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="mt-1"
              required
              data-testid="input-name"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Code</label>
              <Input
                variant="flat"
                placeholder="e.g. ACME"
                maxLength={16}
                value={formData.code || ''}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className="mt-1"
                data-testid="input-code"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Reference Number</label>
              <Input
                variant="flat"
                placeholder="ERP / legacy ID"
                value={formData.accountNumber || ''}
                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                className="mt-1"
                data-testid="input-account-number"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input
                variant="flat"
                type="email"
                placeholder="contact@company.com"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1"
                data-testid="input-email"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Phone</label>
              <Input
                variant="flat"
                placeholder="(555) 123-4567"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="mt-1"
                data-testid="input-phone"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Industry</label>
              <Select
                value={formData.industry || ''}
                onValueChange={(value) => setFormData({ ...formData, industry: value })}
              >
                <SelectTrigger variant="flat" className="mt-1" data-testid="select-industry">
                  <SelectValue placeholder="Select industry" />
                </SelectTrigger>
                <SelectContent>
                  {industries.map((industry) => (
                    <SelectItem key={industry} value={industry}>
                      {industry}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Status</label>
              <Select
                value={formData.status || ''}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger variant="flat" className="mt-1" data-testid="select-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Tier</label>
              <Input
                variant="flat"
                placeholder="e.g. Gold, Silver"
                value={formData.tier || ''}
                onChange={(e) => setFormData({ ...formData, tier: e.target.value })}
                className="mt-1"
                data-testid="input-tier"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Territory</label>
              <Input
                variant="flat"
                placeholder="e.g. Northeast, West Coast"
                value={formData.territory || ''}
                onChange={(e) => setFormData({ ...formData, territory: e.target.value })}
                className="mt-1"
                data-testid="input-territory"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Website</label>
            <Input
              variant="flat"
              placeholder="https://example.com"
              value={formData.website || ''}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="mt-1"
              data-testid="input-website"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Lead Source</label>
              <Input
                variant="flat"
                placeholder="e.g., Referral, Trade Show"
                value={formData.leadSource || ''}
                onChange={(e) => setFormData({ ...formData, leadSource: e.target.value })}
                className="mt-1"
                data-testid="input-lead-source"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Customer Class</label>
              <Select
                value={formData.customerClass || ''}
                onValueChange={(value) => setFormData({ ...formData, customerClass: value })}
              >
                <SelectTrigger variant="flat" className="mt-1" data-testid="select-customer-class">
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="wholesale">Wholesale</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="distributor">Distributor</SelectItem>
                  <SelectItem value="oem">OEM</SelectItem>
                  <SelectItem value="government">Government</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-md border p-3">
            <input
              type="checkbox"
              checked={formData.creditHold || false}
              onChange={(e) => setFormData({ ...formData, creditHold: e.target.checked })}
              className="h-4 w-4 rounded border-input"
              data-testid="checkbox-credit-hold"
            />
            <div>
              <label className="text-sm font-medium cursor-pointer">Credit Hold</label>
              <p className="text-xs text-muted-foreground">Prevent new orders for this account</p>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium">Notes</label>
            <Textarea
              variant="flat"
              placeholder="Any additional notes..."
              className="min-h-[80px] mt-1"
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              data-testid="textarea-notes"
            />
          </div>
        </form>
      </FormDrawer>
    </div>
  );
}
